import React from 'react'
import { styled, Tooltip } from "@material-ui/core";
import { GridColDef, GridColumnGroupingModel, GridRenderCellParams } from "@mui/x-data-grid";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import { Link } from "react-router-dom";
import { StatusTag } from "../../common";
import NoMatch from "../../common/status-indicators/no-match";
import moment from "moment";
import { Close, Done } from "@material-ui/icons";
import { ActionMenu } from "../../common/action-menu";
import { getReportingMonth } from "../../../utils/getReportingMonth";

const currentReportingMonth = getReportingMonth(new Date(), 0, "MMM YYYY");


const HeaderWithIconRoot = styled("div")(({ theme }) => ({
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end !important",
    "& span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      marginRight: theme.spacing(0.5),
    },
  }));

  const HeaderWithIcon = (props) => {
    const { icon, ...params } = props;

    return (
      <HeaderWithIconRoot className="currentReportingMonth">
        {icon}&nbsp;<span>{params.headerName ?? params.groupId}</span>
      </HeaderWithIconRoot>
    );
  };

export function getColumnGroupingModel(isAdmin: boolean, styles): GridColumnGroupingModel {
  return [
    {
      groupId: "internal_data",
      headerName: "",
      headerClassName: `${!isAdmin && styles.borderRight}`,
      children: [
        { field: "name" },
        { field: "teamCode" },
        { field: "codatConnectionStatus" },
        { field: "unResolvedAccounts" },
        { field: "isMonthlyRefreshEnabled" },
        { field: "lastRefreshDate" },
      ],
    },
    {
      groupId: "indicators",
      headerName: "Current Reporting Month",
      renderHeaderGroup: (params) => <HeaderWithIcon {...params} icon={<CalendarTodayIcon fontSize="small" />} />,
      headerClassName: `${styles.borderRight} ${isAdmin && styles.currentMonthIndicatorHeader}`,
      children: [
        { field: "workbookStatus" },
        { field: "bookClosureStatus" },
        { field: "isWorkbookDeliveredToCT" },
        { field: "isWorkbookDeliveredToMCL" },
      ],
    },
    {
      groupId: "companyStatus",
      headerName: "",
      headerClassName: `${styles.hideSepratorAndRightBorder}`,
      children: [{ field: "companyStatus" }],
    },
  ];
}


export function getColumnsConfig(styles, isAdmin: boolean, isMyPortfolio: boolean, filterType: string, toggleHiddenStatusByCompanyID: (actionType: string, rowData: any) => void, modifyMyPortfolioCompanyList: (companyId: string, actionType: string) => void, linkWithSrcInstance): GridColDef<any, any, any>[] {
  return [
    {
      field: "name",
      headerName: "Company Legal Name",
      sortable: true,
      flex: 1,
      minWidth: 200,
      cellClassName: styles.colorBlue,
      headerClassName: styles.hideRightSeparator,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip
          arrow
          title={
            <>
              {params.value ?? "NA"}
              <br />
              {params?.row?.previewCrmEntityIdNCif}
            </>
          }
        >
          <span className={styles.textOverflow}>
            {params?.row?.codatCompanyId ? (
              <Link to={linkWithSrcInstance(`/svber/data-management/company-profile/${params?.row?.codatCompanyId}`)}>
                {" "}
                {params.value ?? "NA"}
              </Link>
            ) : (
              params.value ?? "NA"
            )}
            <p className={styles.cifTextStyle}>{params?.row?.previewCrmEntityIdNCif}</p>
          </span>
        </Tooltip>
      ),
    },
    {
      field: "codatConnectionStatus",
      headerName: "Codat Connection",
      minWidth: 160,
      // maxWidth: 196,
      flex: 1,
      sortable: false,
      headerClassName: styles.hideRightSeparator,
      renderCell: (params: GridRenderCellParams) => (
        <StatusTag caseName="connectionStatus" status={params.value as string} />
      ),
    },
    {
      field: "unResolvedAccounts",
      headerName: "Unbucketed Accounts",
      flex: 1,
      minWidth: 191,
      sortable: false,
      headerClassName: styles.hideRightSeparator,
      cellClassName: styles.placeItemCenter,
      headerAlign: "center",
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <NoMatch
            showIconOnly
            color="action"
            className={styles.marginTootlip}
            tooltipTitle={"Accounts requiring bucketing and/or matching."}
          />
        </>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <>
          {params.value ? (
            <div className={`${params?.value > 0 ? styles.unResolvedAccountsChip : styles.chipStyleSuccess}`}>
              <span>{params.value}</span>
            </div>
          ) : (
            <p style={{ opacity: 0.6 }}>N/A</p>
          )}
        </>
      ),
    },
    {
      field: "isMonthlyRefreshEnabled",
      headerName: "Monthly Refresh",
      flex: 1,
      minWidth: 148,
      sortable: false,
      headerClassName: styles.hideRightSeparator,
      renderCell: (params: GridRenderCellParams) => (
        <StatusTag caseName="isMonthlyRefreshEnabled" status={params.value as string} />
      ),
    },
    {
      field: "lastRefreshDate",
      headerName: "Last Refresh",
      flex: 1,
      minWidth: 188,
      sortable: false,
      headerClassName: styles.hideRightSeparator,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          `${moment.tz(params.value as string, "America/Los_Angeles").format("MMM DD YYYY hh:mm a ")}PT`
        ) : (
          <p style={{ opacity: 0.6 }}>N/A</p>
        ),
    },
    {
      field: "teamCode",
      headerName: "Team Code",
      width: 114,

      sortable: false,
      headerClassName: styles.hideRightSeparator,
      cellClassName: styles.placeItemCenter,
      headerAlign: "center", // Center the header text
      renderCell: (params: GridRenderCellParams) => <div>{params.value}</div>,
    },
    {
      field: "workbookStatus",
      headerName: "Workbook Status",
      minWidth: 172,
      sortable: false,
      headerClassName: styles.borderLeft,
      cellClassName: `${styles.borderLeft}`,
      renderCell: (params: GridRenderCellParams) => (
        <StatusTag status={params.value as string} caseName="workbookStatus">
          {params.value === "Generated" && params.row.workbookCreatedOn ? (
            <p className={styles.statusMessage}>
              <span>
                {moment
                  .tz(params.row.workbookCreatedOn as string, "America/Los_Angeles")
                  .format("MMM DD YYYY hh:mm a ")}
                PT
              </span>
            </p>
          ) : (
            params.value === "Missing" && (
              <p className={styles.statusMessage}>
                <span>For {currentReportingMonth}</span>
              </p>
            )
          )}
        </StatusTag>
      ),
    },
    {
      field: "bookClosureStatus",
      headerName: "Books Closure",
      minWidth: 142,
      sortable: false,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <NoMatch
            showIconOnly
            color="action"
            className={styles.marginTootlip}
            tooltipTitle={
              "Tracks the variance of certain indicators relative to the previous reporting month. A higher indicator variance is a stronger signal that the borrower's books may not be closed."
            }
          />
        </>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <StatusTag caseName="bookClosureStatus" status={params.value as string}></StatusTag>
      ),
    },
    {
      field: "isWorkbookDeliveredToCT",
      headerName: "Sent to CT",
      minWidth: 117,
      sortable: false,
      headerAlign: "center", // Center the header text
      cellClassName: styles.placeItemCenter,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <NoMatch
            showIconOnly
            color="action"
            className={styles.marginTootlip}
            tooltipTitle={"Sent to Compliance Tracker."}
          />
        </>
      ),
      renderCell: (params: GridRenderCellParams) => {
        if (typeof params?.value !== "boolean") {
          return null; // Don't render anything if value is not a boolean
        }

        return (
          <>
            <Tooltip title={params.value ? "Success" : "Failed"} arrow placement="bottom">
              {params.value ? <Done className={styles.fillBlackColor} /> : <Close className={styles.colorRed} />}
            </Tooltip>
          </>
        );
      },
    },
    {
      field: "isWorkbookDeliveredToMCL",
      headerName: "Sent to MCL",
      minWidth: 129,
      sortable: false,
      headerAlign: "center", // Center the header text
      headerClassName: `${styles.hideSepratorAndRightBorder}`,
      cellClassName: styles.placeItemCenter,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <NoMatch
            showIconOnly
            color="action"
            className={styles.marginTootlip}
            tooltipTitle={"Sent to Moody's CreditLens."}
          />
        </>
      ),
      renderCell: (params: GridRenderCellParams) => {
        if (typeof params?.value !== "boolean") {
          return null; // Don't render anything if value is not a boolean
        }

        return (
          <>
            <Tooltip title={params.value ? "Success" : "Failed"} arrow placement="bottom">
              {params.value ? <Done className={styles.fillBlackColor} /> : <Close className={styles.colorRed} />}
            </Tooltip>
          </>
        );
      },
    },
    {
      field: "companyStatus",
      headerName: "Status",
      width: 155,
      cellClassName: `${styles.customStatusColumn} ${styles.borderLeft} ${styles.borderRight}`,
      headerClassName: `${styles.hideSepratorAndRightBorder}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
          <>
            {params.row.hidden && <StatusTag caseName="clientStatus" status={'Hidden'}></StatusTag>}
            <StatusTag caseName="clientStatus" status={params.row.status as string}></StatusTag>
          </>
      ),
    },
    // @ts-ignore
    ...(isAdmin
      ? [{
            field: "actions",
            headerName: isAdmin && "Actions",
            width: 205,
            sortable: false,
            headerClassName: `${styles.hideRightSeparator} admin-view`,
            renderCell: (params: GridRenderCellParams) => (
              <ActionMenu
                isAdmin={isAdmin}
                filterType={filterType}
                name={params?.row?.name}
                companyIsHidden={params?.row?.hidden}
                handleHiddenStateChange={(actionType: string) => toggleHiddenStatusByCompanyID(actionType, params?.row)}
                handleUpdateCompanyList={(actionType) => modifyMyPortfolioCompanyList(params.row.companyId, actionType)}
                isAddedToPortfolio={params?.row?.isAddedToPortfolio}
              />
            ),
          },
        ]
      : [
          //for CA and CS users the display is differently formatted
          {
            field: "actions",
            headerName: "",
            width: 131,
            sortable: false,
            cellClassName: styles.borderLeft,
            headerClassName: `${styles.borderLeft} ${styles.hideRightSeparator}`,
            renderCell: (params: GridRenderCellParams) => (
              <ActionMenu
                filterType={filterType}
                name={params?.row?.name}
                companyIsHidden={params?.row?.hidden}
                isAdmin={isAdmin}
                handleUpdateCompanyList={(actionType) => modifyMyPortfolioCompanyList(params.row.companyId, actionType)}
                isAddedToPortfolio={params?.row?.isAddedToPortfolio}
              />
            ),
          },
        ]),
  ];
}
